// Tarjeta de contacto de la ficha (`.contact-card` del mock): ícono, rótulo, valor y «Copiar».
import { useEffect, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Lo que dura el «Copiado» en verde, como el mock. */
const COPIED_FEEDBACK_MS = 1400;

export function CustomerContactCard({
    icon,
    label,
    value,
    copyValue,
}: {
    icon: ReactNode;
    label: string;
    /** `null` = sin registrar: se dice en itálica y no hay nada que copiar. */
    value: string | null;
    /** Lo que va al portapapeles; por omisión, el valor que se ve. */
    copyValue?: string;
}) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const id = setTimeout(() => {
            setCopied(false);
        }, COPIED_FEEDBACK_MS);
        return () => {
            clearTimeout(id);
        };
    }, [copied]);

    const missing = value === null;
    const subject = label.toLowerCase();

    return (
        <div
            className={cn(
                'flex min-w-0 items-center gap-2.5 rounded-md border border-border bg-muted/35 px-2.5 py-2',
                missing && 'opacity-50',
            )}
        >
            <span className="flex size-7 flex-none items-center justify-center rounded-[5px] bg-primary/10 text-primary">
                {icon}
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                    {label}
                </div>
                <div
                    className={cn(
                        'mt-px truncate text-[13px] tabular-nums',
                        missing
                            ? 'font-normal italic text-muted-foreground'
                            : 'font-semibold text-foreground',
                    )}
                    title={value ?? undefined}
                >
                    {value ?? 'sin registrar'}
                </div>
            </div>
            {missing ? null : (
                <button
                    type="button"
                    aria-label={`Copiar ${subject}`}
                    onClick={() => {
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- falta en contextos no seguros y en jsdom
                        if (!navigator.clipboard) return;
                        // El «Copiado» solo se prende si la copia resolvió.
                        void navigator.clipboard.writeText(copyValue ?? value).then(() => {
                            setCopied(true);
                        });
                    }}
                    className={cn(
                        'inline-flex flex-none items-center gap-1 rounded border px-2 py-1 text-[10.5px] transition-colors',
                        copied
                            ? 'border-success-soft-border bg-success-soft text-success'
                            : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                >
                    {copied ? (
                        <Check className="size-3" aria-hidden />
                    ) : (
                        <Copy className="size-3" aria-hidden />
                    )}
                    {copied ? 'Copiado' : 'Copiar'}
                </button>
            )}
            <span className="sr-only" role="status" aria-live="polite">
                {copied ? `${label} copiado al portapapeles` : ''}
            </span>
        </div>
    );
}
